<template>
	<div class="api-documentation">
		<div class="doc-header">
			<h2>{{ $t('apiDocumentation') }}</h2>
			<p class="doc-subtitle">{{ $t('apiDocumentationSubtitle') }}</p>
		</div>

		<div class="doc-section">
			<h3>{{ $t('authentication') }}</h3>
			<p>{{ $t('authenticationDescription') }}</p>

			<div class="code-block">
				<div class="code-header">
					<span>{{ $t('example') }}</span>
					<button @click="copyCode('auth')" class="copy-btn">
						<i class="fas fa-copy"></i>
						{{ copied === 'auth' ? $t('copied') : $t('copy') }}
					</button>
				</div>
				<pre><code>Authorization: Bearer sf_abc123def456ghi789jkl012mno345pqr678stu</code></pre>
			</div>
		</div>

		<div class="doc-section">
			<h3>{{ $t('exampleRequests') }}</h3>

			<!-- Exemplo cURL -->
			<div class="example-block">
				<h4>cURL</h4>
				<div class="code-block">
					<div class="code-header">
						<span>GET Request</span>
						<button @click="copyCode('curl-get')" class="copy-btn">
							<i class="fas fa-copy"></i>
							{{ copied === 'curl-get' ? $t('copied') : $t('copy') }}
						</button>
					</div>
					<pre><code>curl -X GET "{{ apiBaseUrl }}/api/endpoint" \
  -H "Authorization: Bearer YOUR_API_KEY" \
  -H "Content-Type: application/json"</code></pre>
				</div>

				<div class="code-block">
					<div class="code-header">
						<span>POST Request</span>
						<button @click="copyCode('curl-post')" class="copy-btn">
							<i class="fas fa-copy"></i>
							{{ copied === 'curl-post' ? $t('copied') : $t('copy') }}
						</button>
					</div>
					<pre><code>curl -X POST "{{ apiBaseUrl }}/api/endpoint" \
  -H "Authorization: Bearer YOUR_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{"key": "value"}'</code></pre>
				</div>
			</div>

			<!-- Exemplo JavaScript/Fetch -->
			<div class="example-block">
				<h4>JavaScript (Fetch API)</h4>
				<div class="code-block">
					<div class="code-header">
						<span>GET Request</span>
						<button @click="copyCode('js-get')" class="copy-btn">
							<i class="fas fa-copy"></i>
							{{ copied === 'js-get' ? $t('copied') : $t('copy') }}
						</button>
					</div>
					<pre><code>fetch('{{ apiBaseUrl }}/api/endpoint', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));</code></pre>
				</div>

				<div class="code-block">
					<div class="code-header">
						<span>POST Request</span>
						<button @click="copyCode('js-post')" class="copy-btn">
							<i class="fas fa-copy"></i>
							{{ copied === 'js-post' ? $t('copied') : $t('copy') }}
						</button>
					</div>
					<pre><code>fetch('{{ apiBaseUrl }}/api/endpoint', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ key: 'value' })
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));</code></pre>
				</div>
			</div>

			<!-- Exemplo Python -->
			<div class="example-block">
				<h4>Python (Requests)</h4>
				<div class="code-block">
					<div class="code-header">
						<span>GET Request</span>
						<button @click="copyCode('python-get')" class="copy-btn">
							<i class="fas fa-copy"></i>
							{{ copied === 'python-get' ? $t('copied') : $t('copy') }}
						</button>
					</div>
					<pre><code>import requests

url = "{{ apiBaseUrl }}/api/endpoint"
headers = {
    "Authorization": "Bearer YOUR_API_KEY",
    "Content-Type": "application/json"
}

response = requests.get(url, headers=headers)
print(response.json())</code></pre>
				</div>

				<div class="code-block">
					<div class="code-header">
						<span>POST Request</span>
						<button @click="copyCode('python-post')" class="copy-btn">
							<i class="fas fa-copy"></i>
							{{ copied === 'python-post' ? $t('copied') : $t('copy') }}
						</button>
					</div>
					<pre><code>import requests
import json

url = "{{ apiBaseUrl }}/api/endpoint"
headers = {
    "Authorization": "Bearer YOUR_API_KEY",
    "Content-Type": "application/json"
}
data = {"key": "value"}

response = requests.post(url, headers=headers, json=data)
print(response.json())</code></pre>
				</div>
			</div>

			<!-- Exemplo C# -->
			<div class="example-block">
				<h4>C# (HttpClient)</h4>
				<div class="code-block">
					<div class="code-header">
						<span>GET Request</span>
						<button @click="copyCode('csharp-get')" class="copy-btn">
							<i class="fas fa-copy"></i>
							{{ copied === 'csharp-get' ? $t('copied') : $t('copy') }}
						</button>
					</div>
					<pre><code>using System.Net.Http;
using System.Net.Http.Headers;

var client = new HttpClient();
client.DefaultRequestHeaders.Authorization =
    new AuthenticationHeaderValue("Bearer", "YOUR_API_KEY");

var response = await client.GetAsync("{{ apiBaseUrl }}/api/endpoint");
var content = await response.Content.ReadAsStringAsync();</code></pre>
				</div>

				<div class="code-block">
					<div class="code-header">
						<span>POST Request</span>
						<button @click="copyCode('csharp-post')" class="copy-btn">
							<i class="fas fa-copy"></i>
							{{ copied === 'csharp-post' ? $t('copied') : $t('copy') }}
						</button>
					</div>
					<pre><code>using System.Net.Http;
using System.Net.Http.Headers;
using System.Text;
using System.Text.Json;

var client = new HttpClient();
client.DefaultRequestHeaders.Authorization =
    new AuthenticationHeaderValue("Bearer", "YOUR_API_KEY");

var data = new { key = "value" };
var json = JsonSerializer.Serialize(data);
var content = new StringContent(json, Encoding.UTF8, "application/json");

var response = await client.PostAsync("{{ apiBaseUrl }}/api/endpoint", content);
var result = await response.Content.ReadAsStringAsync();</code></pre>
				</div>
			</div>
		</div>

		<div class="doc-section">
			<h3>{{ $t('accessLevels') }}</h3>
			<div class="access-levels-info">
				<div class="level-card">
					<i class="fas fa-eye"></i>
					<h4>{{ $t('readOnly') }}</h4>
					<p>{{ $t('readOnlyDescription') }}</p>
					<ul>
						<li>GET requests</li>
						<li>{{ $t('readOnlyPermissions') }}</li>
					</ul>
				</div>
				<div class="level-card">
					<i class="fas fa-edit"></i>
					<h4>{{ $t('standard') }}</h4>
					<p>{{ $t('standardDescription') }}</p>
					<ul>
						<li>GET, POST, PUT requests</li>
						<li>{{ $t('standardPermissions') }}</li>
					</ul>
				</div>
				<div class="level-card">
					<i class="fas fa-user-shield"></i>
					<h4>{{ $t('admin') }}</h4>
					<p>{{ $t('adminDescription') }}</p>
					<ul>
						<li>GET, POST, PUT, DELETE requests</li>
						<li>{{ $t('adminPermissions') }}</li>
					</ul>
				</div>
			</div>
		</div>

		<div class="doc-section">
			<h3>{{ $t('bestPractices') }}</h3>
			<ul class="best-practices-list">
				<li>
					<i class="fas fa-check-circle"></i>
					{{ $t('bestPractice1') }}
				</li>
				<li>
					<i class="fas fa-check-circle"></i>
					{{ $t('bestPractice2') }}
				</li>
				<li>
					<i class="fas fa-check-circle"></i>
					{{ $t('bestPractice3') }}
				</li>
				<li>
					<i class="fas fa-check-circle"></i>
					{{ $t('bestPractice4') }}
				</li>
				<li>
					<i class="fas fa-check-circle"></i>
					{{ $t('bestPractice5') }}
				</li>
			</ul>
		</div>

		<div class="doc-section error-section">
			<h3>{{ $t('commonErrors') }}</h3>
			<div class="error-example">
				<h4>401 Unauthorized</h4>
				<p>{{ $t('error401Description') }}</p>
				<div class="code-block small">
					<pre><code>{ "error": "Invalid or missing API key" }</code></pre>
				</div>
			</div>
			<div class="error-example">
				<h4>403 Forbidden</h4>
				<p>{{ $t('error403Description') }}</p>
				<div class="code-block small">
					<pre><code>{ "error": "Insufficient permissions" }</code></pre>
				</div>
			</div>
			<div class="error-example">
				<h4>429 Too Many Requests</h4>
				<p>{{ $t('error429Description') }}</p>
				<div class="code-block small">
					<pre><code>{ "error": "Rate limit exceeded" }</code></pre>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ApiKeyDocumentation',
	data() {
		return {
			copied: null,
			apiBaseUrl: process.env.VUE_APP_INTEGRACOES_API || 'https://api.example.com',
		}
	},
	methods: {
		copyCode(codeId) {
			const codeBlocks = {
				auth: 'Authorization: Bearer sf_abc123def456ghi789jkl012mno345pqr678stu',
				'curl-get': `curl -X GET "${this.apiBaseUrl}/api/endpoint" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`,
				'curl-post': `curl -X POST "${this.apiBaseUrl}/api/endpoint" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"key": "value"}'`,
				'js-get': `fetch('${this.apiBaseUrl}/api/endpoint', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));`,
				'js-post': `fetch('${this.apiBaseUrl}/api/endpoint', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ key: 'value' })
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));`,
				'python-get': `import requests

url = "${this.apiBaseUrl}/api/endpoint"
headers = {
    "Authorization": "Bearer YOUR_API_KEY",
    "Content-Type": "application/json"
}

response = requests.get(url, headers=headers)
print(response.json())`,
				'python-post': `import requests
import json

url = "${this.apiBaseUrl}/api/endpoint"
headers = {
    "Authorization": "Bearer YOUR_API_KEY",
    "Content-Type": "application/json"
}
data = {"key": "value"}

response = requests.post(url, headers=headers, json=data)
print(response.json())`,
				'csharp-get': `using System.Net.Http;
using System.Net.Http.Headers;

var client = new HttpClient();
client.DefaultRequestHeaders.Authorization =
    new AuthenticationHeaderValue("Bearer", "YOUR_API_KEY");

var response = await client.GetAsync("${this.apiBaseUrl}/api/endpoint");
var content = await response.Content.ReadAsStringAsync();`,
				'csharp-post': `using System.Net.Http;
using System.Net.Http.Headers;
using System.Text;
using System.Text.Json;

var client = new HttpClient();
client.DefaultRequestHeaders.Authorization =
    new AuthenticationHeaderValue("Bearer", "YOUR_API_KEY");

var data = new { key = "value" };
var json = JsonSerializer.Serialize(data);
var content = new StringContent(json, Encoding.UTF8, "application/json");

var response = await client.PostAsync("${this.apiBaseUrl}/api/endpoint", content);
var result = await response.Content.ReadAsStringAsync();`,
			}

			const code = codeBlocks[codeId]
			if (code) {
				navigator.clipboard.writeText(code).then(() => {
					this.copied = codeId
					setTimeout(() => {
						this.copied = null
					}, 2000)
				})
			}
		},
	},
}
</script>

<style scoped>
.api-documentation {
	max-width: 1200px;
	margin: 0 auto;
	padding: 2rem;
}

.doc-header {
	margin-bottom: 3rem;
}

.doc-header h2 {
	font-size: 2rem;
	color: var(--color-navy-blue);
	margin-bottom: 0.5rem;
}

.doc-subtitle {
	font-size: 1.1rem;
	color: #666;
}

.doc-section {
	margin-bottom: 3rem;
	padding: 2rem;
	background: white;
	border-radius: 8px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.doc-section h3 {
	font-size: 1.5rem;
	color: var(--color-navy-blue);
	margin-bottom: 1rem;
	border-bottom: 2px solid var(--color-green);
	padding-bottom: 0.5rem;
}

.doc-section h4 {
	font-size: 1.2rem;
	color: var(--color-navy-blue);
	margin-top: 1.5rem;
	margin-bottom: 1rem;
}

.code-block {
	background: #f8f9fa;
	border-radius: 8px;
	overflow: hidden;
	margin: 1rem 0;
	border: 1px solid #e0e0e0;
}

.code-block.small {
	margin-top: 0.5rem;
}

.code-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.75rem 1rem;
	background: #e9ecef;
	border-bottom: 1px solid #dee2e6;
}

.code-header span {
	font-weight: 600;
	color: #495057;
	font-size: 0.9rem;
}

.copy-btn {
	background: var(--color-green);
	color: white;
	border: none;
	padding: 0.4rem 0.8rem;
	border-radius: 4px;
	cursor: pointer;
	font-size: 0.85rem;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	transition: background 0.2s;
}

.copy-btn:hover {
	background: #28a745;
}

.code-block pre {
	margin: 0;
	padding: 1rem;
	overflow-x: auto;
}

.code-block code {
	font-family: 'Courier New', monospace;
	font-size: 0.9rem;
	color: #212529;
	line-height: 1.6;
}

.example-block {
	margin-bottom: 2rem;
}

.access-levels-info {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
	gap: 1.5rem;
	margin-top: 1.5rem;
}

.level-card {
	padding: 1.5rem;
	background: #f8f9fa;
	border-radius: 8px;
	border: 2px solid #e0e0e0;
	transition: transform 0.2s, box-shadow 0.2s;
}

.level-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.level-card i {
	font-size: 2rem;
	color: var(--color-green);
	margin-bottom: 1rem;
}

.level-card h4 {
	margin-top: 0;
	margin-bottom: 0.5rem;
	color: var(--color-navy-blue);
}

.level-card p {
	color: #666;
	margin-bottom: 1rem;
	font-size: 0.95rem;
}

.level-card ul {
	list-style: none;
	padding: 0;
	margin: 0;
}

.level-card ul li {
	padding: 0.3rem 0;
	color: #555;
	font-size: 0.9rem;
}

.best-practices-list {
	list-style: none;
	padding: 0;
}

.best-practices-list li {
	display: flex;
	align-items: flex-start;
	gap: 1rem;
	padding: 1rem;
	background: #f8f9fa;
	border-radius: 8px;
	margin-bottom: 1rem;
}

.best-practices-list i {
	color: var(--color-green);
	font-size: 1.2rem;
	margin-top: 0.2rem;
	flex-shrink: 0;
}

.error-section {
	background: #fff3cd;
	border-left: 4px solid #ffc107;
}

.error-example {
	margin-bottom: 1.5rem;
}

.error-example:last-child {
	margin-bottom: 0;
}

.error-example h4 {
	color: #856404;
	margin-top: 0;
}

.error-example p {
	color: #856404;
	margin-bottom: 0.5rem;
}
</style>
